


















































import { computed, defineComponent, getCurrentInstance, onBeforeMount, ref, watch } from "@vue/composition-api";
import Toolbar from "../components/toolbar/Toolbar.vue";
import Infoland3ColContainer from "@/components/generic/infoland-3col-container.vue";
import CollectionFilterContainer from "@/components/ai-results/CollectionFilterContainer.vue";
import BrowseResultsContainer from "@/components/ai-browse/BrowseResultsContainer.vue";
import storeHelper from "@/store/store-helper";
import AiSearchBar from "@/components/ai-search/AiSearchBar.vue";
import { useScroll } from "@/composables/useScroll";
import { Feature } from "@/types/Feature";
import { FeatureType } from "@/enums";

export default defineComponent({
    name: "AIBrowse",
    props: {
        collectionId: { type: [Number, null], default: null },
        portalId: { type: [Number, null], required: true }
    },
    components: {
        Toolbar,
        Infoland3ColContainer,
        CollectionFilterContainer,
        BrowseResultsContainer,
        AiSearchBar
    },
    setup(props) {
        const router = getCurrentInstance().proxy.$router;
        const isBrowseModeHybrid = ref<boolean>(false);

        /**
         * The name of the portal to which the search results belong
         */
        const portalName = computed(() => {
            return storeHelper.portalStore.getPortal(props.portalId).name;
        });

        /**
         * Load the features and check
         */
        onBeforeMount(() => {
            setHybridBrowseMode();
        });

        //get feedback from container component if the content items are loaded
        const portalCollectionIds = ref<number[] | undefined>(undefined);
        const onContentItemsLoaded = async (loaded: boolean) => {
            if (!loaded) return;
            portalCollectionIds.value = storeHelper.aiBrowseStore.getCollectionIds(props.portalId, props.collectionId);
        };

        /**
         * Check if the browse mode is hybrid
         */
        const setHybridBrowseMode = async () => {
            let features: Feature[] = [];
            const cachedFeatures = storeHelper.FeaturesStore.features;
            if (cachedFeatures.length > 0) features = cachedFeatures;
            else features = await storeHelper.FeaturesStore.loadCurrentFeatures();

            const isBrowseLegacyEnabled = features.find(f => f.name === FeatureType.browse_mode_legacy)?.enabled ?? false;
            const isBrowseModernEnabled = features.find(f => f.name === FeatureType.browse_mode_legacy)?.enabled ?? false;
            isBrowseModeHybrid.value = isBrowseLegacyEnabled && isBrowseModernEnabled;
        };

        /**
         * Go to the AI search page when you want to search for something
         */
        const handleSearchTextChanged = (searchText: string) => {
            if (searchText == null || searchText.trim() == "") return;
            router.push({
                name: "AISearch",
                query: {
                    searchText: searchText,
                    collectionId: props.collectionId?.toString()
                }
            });
        };

        /**
         * updates the route and reflects the search filters in the url
         */
        const updateBrowseRoute = (collectionId: number) => {
            let routeChange: any = {
                name: "AIBrowse",
                params: { portalId: props.portalId.toString() },
                query: {},
                replace: false
            };

            if (collectionId != null) {
                routeChange.query.collectionId = collectionId;
                delete routeChange.params.portalId;
            }

            //do not change route if you are already on the same route
            const route = router.currentRoute;
            const isSameRoute =
                route.name === routeChange.name &&
                JSON.stringify(route.params) === JSON.stringify(routeChange.params) &&
                JSON.stringify(route.query) === JSON.stringify(routeChange.query);
            if (isSameRoute) return;

            router.push(routeChange);
        };

        /**
         * handle the back button navigation
         */
        const onBackNavigation = () => {
            router.go(-1);
        };

        /**
         * Go back to the old version of the browse page
         */
        const GoBackToOldVersion = () => {
            router.push({
                name: "Browse",
                params: { portalId: props.portalId.toString() }
            });
        };

        /**
         * The direction of the scroll needed for UI animations
         */
        const { direction: scrollDirection } = useScroll({ directionChangeTreshold: 36 });
        const isScrollingUp = computed(() => scrollDirection.value === "up");

        return {
            handleSearchTextChanged,
            onBackNavigation,
            GoBackToOldVersion,
            updateBrowseRoute,
            onContentItemsLoaded,
            portalName,
            isScrollingUp,
            isBrowseModeHybrid,
            portalCollectionIds
        };
    }
});
